import React from 'react';
import * as Sentry from '@sentry/react';
import { IonButton, IonToast } from '@ionic/react';
import { useEffect, useState } from 'react';

/**
 * A simple component to test Sentry integration
 * Mobile-first design with proper spacing and styling
 */
const SentryDebugButton: React.FC = () => {
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [sentryStatus, setSentryStatus] = useState<'unknown' | 'working' | 'failed'>('unknown');

  // Test Sentry connection on mount
  useEffect(() => {
    // Add a breadcrumb to test Sentry
    Sentry.addBreadcrumb({
      category: 'test',
      message: 'SentryDebugButton mounted',
      level: 'info',
    });
    
    // Generate a test event ID
    const testEventId = Sentry.captureMessage('Sentry connection test', {
      level: 'info',
    });
    
    if (testEventId) {
      console.log('Sentry test event ID:', testEventId);
      setSentryStatus('working');
    } else {
      console.error('Failed to generate Sentry event ID');
      setSentryStatus('failed');
    }
  }, []);

  const testSentry = () => {
    try {
      // Create a test error
      throw new Error('This is a test error from SentryDebugButton');
    } catch (error) {
      // Capture the error
      const eventId = Sentry.captureException(error);
      
      // Show result
      if (eventId) {
        setToastMessage(`Error sent to Sentry! Event ID: ${eventId}`);
        console.log('Sentry event ID:', eventId);
      } else {
        setToastMessage('Failed to send error to Sentry');
        console.error('Failed to generate Sentry event ID');
      }
      
      setShowToast(true);
    }
  };

  return (
    <div className="p-4 flex flex-col items-center">
      <div className="mb-4 text-center">
        <div className="text-sm font-medium mb-2">
          Sentry Status: 
          <span className={`ml-2 ${
            sentryStatus === 'working' ? 'text-green-600' : 
            sentryStatus === 'failed' ? 'text-red-600' : 'text-gray-600'
          }`}>
            {sentryStatus === 'working' ? 'Connected' : 
             sentryStatus === 'failed' ? 'Connection Failed' : 'Checking...'}
          </span>
        </div>
      </div>
      
      <IonButton 
        expand="block"
        color="danger"
        onClick={testSentry}
        className="w-full max-w-xs"
      >
        Test Sentry Error
      </IonButton>
      
      <IonToast
        isOpen={showToast}
        onDidDismiss={() => setShowToast(false)}
        message={toastMessage}
        duration={3000}
        position="bottom"
      />
    </div>
  );
};

export default SentryDebugButton;
